import React, { Fragment, useReducer } from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import Select from "react-select";

import TextInput from "../text-input";
import countryCodes from "../../helpers/country-codes";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};


const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: undefined,
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zip: "",
        country: { value: "US", label: "United States" },
      };
    }
    default:
      return state;
  }
};

const SingleProspectAddressesTable = (props) => {
  const { addresses = [], handleChange, handleToggleEdit, disabled = false } = props;
  const [primaryAddress, ...otherAddresses] = addresses;

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: undefined,
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: { value: "US", label: "United States" },
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddAddress = (e) => {
    e.preventDefault();

    handleToggleEdit("addresses");
    dispatch({
      type: "openModal",
    });
  };

  const handleEditAddress = (e, index) => {
    e.preventDefault();

    const address = addresses[index];
    if (!address) {
      return;
    }

    handleToggleEdit("addresses");
    dispatch({
      type: "openModal",
      payload: {
        ...address,
        country: countryCodes.find(({ value }) => value === primaryAddress.country),
      },
    });
  };

  const handleDeleteAddress = (e, index) => {
    e.preventDefault();

    const address = addresses[index];
    if (!address) {
      return;
    }

    handleToggleEdit("addresses");
    dispatch({
      type: "openDeleteModal",
      payload: address,
    });
  };

  const handleCloseModal = () => {
    handleToggleEdit();
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleCountryChange = (countryCode) => {
    dispatch({
      type: "fieldChanged",
      field: "country",
      value: countryCode,
    });
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();

    if (!state.addressLine1 || !state.city || !state.zip || !state.country) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    const address = {
      _id: state._id,
      addressLine1: state.addressLine1,
      addressLine2: state.addressLine2,
      city: state.city,
      state: state.state,
      zip: state.zip,
      country: state.country?.value,
    };

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange(
      state._id ?
        (addresses || []).map((item) => item._id === state._id ? address : item) :
        [...(addresses || []), address],
    );
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  const handleDeleteAddressConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange((addresses || []).filter((item) => item._id !== state._id));
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  return (
    <Fragment>
      <tr className="heading-row">
        <td colSpan={3}>Primary Address</td>
        <td/>
      </tr>

      {!addresses.length && <tr>
        <td colSpan={3}>No address is saved yet</td>
        <td/>
      </tr>}

      {primaryAddress && <tr>
        <td colSpan={3}>
          <div className="prospect-multi-rows-data">
            {[
              primaryAddress.addressLine1,
              primaryAddress.addressLine2,
              [primaryAddress.city, primaryAddress.state, primaryAddress.zip].filter(Boolean).join(", "),
              countryCodes.find(({ value }) => value === primaryAddress.country)?.label,
            ].filter(Boolean).map((line, i) => (
              <Fragment key={i}>{i > 0 && <br/>}{line}</Fragment>
            ))}
          </div>
        </td>
        <td>
          <button
            className="base-button icon-button evo-blue"
            onClick={(e) => {
              handleEditAddress(e, 0);
            }}
            disabled={disabled}
          >
            <span className="material-icons">edit</span>
          </button>

          <button
            className="base-button icon-button btn-danger ml-2"
            onClick={(e) => {
              handleDeleteAddress(e, 0);
            }}
            disabled={disabled}
          >
            <span className="material-icons">delete</span>
          </button>
        </td>
      </tr>}

      {otherAddresses.length > 0 && otherAddresses.map((address, index) => (
        <Fragment key={address._id}>
          <tr className="heading-row">
            <td colSpan={3}>Other Address</td>
            <td/>
          </tr>

          <tr>
            <td colSpan={3}>
              <div className="prospect-multi-rows-data">
                {[
                  address.addressLine1,
                  address.addressLine2,
                  [address.city, address.state, address.zip].filter(Boolean).join(", "),
                  countryCodes.find(({ value }) => value === address.country)?.label,
                ].filter(Boolean).map((line, i) => (
                  <Fragment key={i}>{i > 0 && <br/>}{line}</Fragment>
                ))}
              </div>
            </td>
            <td>
              <button
                className="base-button icon-button evo-blue"
                onClick={(e) => {
                  handleEditAddress(e, index + 1);
                }}
                disabled={disabled}
              >
                <span className="material-icons">edit</span>
              </button>

              <button
                className="base-button icon-button btn-danger ml-2"
                onClick={(e) => {
                  handleDeleteAddress(e, index + 1);
                }}
                disabled={disabled}
              >
                <span className="material-icons">delete</span>
              </button>
            </td>
          </tr>
        </Fragment>
      ))}

      <tr>
        <td colSpan={3}>
          <button
            className="mt-20 mb-20 action-button base-button base-blue-button"
            onClick={handleAddAddress}
            disabled={disabled}
          >
            Add Address
          </button>
        </td>
        <td/>
      </tr>


      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxHeight: "100vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{state._id ? "Edit" : "Add"} Address</h4>
        </div>

        <div className="modal-body base-modal">
          <form onSubmit={handleUpdateAddress} className="w-100 base-form mt-20">
            <TextInput
              name="addressLine1"
              label="Address Line 1"
              required
              handleChange={handleInputChange}
              value={state.addressLine1}
            />

            <TextInput
              name="addressLine2"
              label="Address Line 2"
              handleChange={handleInputChange}
              value={state.addressLine2}
            />

            <div className="w-100 d-flex gap-20">
              <TextInput
                name="city"
                label="City"
                required
                handleChange={handleInputChange}
                value={state.city}
                containerClasses="flex-half"
                className="mb-0"
              />

              <TextInput
                name="state"
                label="State/Region/Province"
                handleChange={handleInputChange}
                value={state.state}
                containerClasses="flex-half"
                className="mb-0"
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <TextInput
                name="zip"
                label="ZIP/Postal Code"
                required
                handleChange={handleInputChange}
                value={state.zip}
                containerClasses="flex-half"
                className="mb-0"
              />

              <div className="form-group flex-half">
                <label>Country <span className="required">*</span></label>

                <Select
                  className="countryCode-select"
                  classNamePrefix="select"
                  options={countryCodes}
                  placeholder="Country"
                  onChange={handleCountryChange}
                  value={state.country}
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div className="w-100 mt-20 d-flex gap-20">
              <button className="base-button evo-blue flex-half" type="submit" disabled={state.saving}>
                Submit
              </button>

              <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                      disabled={state.saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Address</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This address will be permanently removed from the prospect’s profile.<br/>
            Do you want to continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <button className="base-button btn-danger flex-half" type="button" onClick={handleDeleteAddressConfirm}
                    disabled={state.saving}>
              Delete Address
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default SingleProspectAddressesTable;
